/**
 * Dual identity card on the Company Profile → Settings tab (CR-7255).
 *
 * Platform staff say here that a customer company also trades as a supplier.
 * It is never a bare switch: turning it on asks for the customer the company
 * supplies and the supplier code, because that pairing is what lets an ERP
 * supplier feed resolve this company by code instead of creating a duplicate
 * supplier of the same name. Turning it off keeps the relationships — they
 * carry orders — and only removes the second portal side.
 *
 * Rendered for Helix users on customer-typed companies only (decision 2a).
 */
import { useCallback, useEffect, useState } from 'react';
import { ArrowLeftRight, CheckCircle, Loader2, AlertCircle, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Switch } from '../../components/ui/switch';
import { coreAPI } from '../../lib/api';
import type { Company, DualIdentityState } from '../../lib/api';
import { getApiErrorMessage } from '../../lib/api/types/common';

interface DualIdentityCardProps {
  company: Company;
  /** Re-read the company so `is_dual_sided` on the page follows a save. */
  onCompanyRefresh: () => void | Promise<void>;
}

export function DualIdentityCard({ company, onCompanyRefresh }: DualIdentityCardProps) {
  const [state, setState] = useState<DualIdentityState | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  // The setup form, shown while the marker is off and the switch was flipped.
  const [arming, setArming] = useState(false);
  const [search, setSearch] = useState('');
  const [candidates, setCandidates] = useState<Company[]>([]);
  const [searching, setSearching] = useState(false);
  const [customer, setCustomer] = useState<Company | null>(null);
  const [supplierCode, setSupplierCode] = useState('');

  const isCustomerCompany = company.company_type === 'customer';

  const load = useCallback(async () => {
    // Decision 2a: a supplier-typed company has no second side to offer, so
    // there is nothing to fetch either.
    if (!isCustomerCompany) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await coreAPI.getDualIdentity(company.id);
      setState(res.data);
    } catch (err: unknown) {
      console.error('Failed to load dual identity', err);
      setError(getApiErrorMessage(err, 'Failed to load the supplier-side status.'));
    } finally {
      setLoading(false);
    }
  }, [company.id, isCustomerCompany]);

  useEffect(() => { void load(); }, [load]);

  // Customer picker: type to filter, click to choose. Same shape as the
  // Change Company dialog so staff meet one picker, not two.
  useEffect(() => {
    if (!arming) return;
    setSearching(true);
    const timer = setTimeout(async () => {
      try {
        const res = await coreAPI.getCompanies({
          search, company_type: 'customer', is_active: true, page_size: 8,
        });
        const data = res.data as unknown as { results?: Company[] };
        const rows = (data.results ?? (res.data as unknown as Company[])) || [];
        setCandidates(rows.filter((c) => c.id !== company.id));
      } catch (err: unknown) {
        console.error('Failed to search customers', err);
      } finally {
        setSearching(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [arming, search, company.id]);

  const apply = async (payload: { is_also_supplier: boolean; customer_id?: number; supplier_code?: string }) => {
    setSaving(true);
    try {
      const res = await coreAPI.setDualIdentity(company.id, payload);
      setState(res.data);
      toast.success(res.data.message || 'Saved');
      setArming(false);
      setCustomer(null);
      setSupplierCode('');
      setSearch('');
      await onCompanyRefresh();
    } catch (err: unknown) {
      toast.error(getApiErrorMessage(err, 'Failed to update the supplier-side status.'));
    } finally {
      setSaving(false);
    }
  };

  if (!isCustomerCompany) return null;

  const on = Boolean(state?.is_also_supplier);
  const canSubmit = Boolean(customer) && supplierCode.trim().length > 0 && !saving;

  return (
    <div className="border-t pt-4" data-testid="dual-identity-card">
      <div className="flex items-start gap-3">
        <ArrowLeftRight className="h-4 w-4 text-muted-foreground mt-0.5" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-3">
            <div>
              <p className="text-sm font-medium">Also trades as supplier</p>
              <p className="text-sm text-muted-foreground">
                {loading
                  ? 'Loading…'
                  : on
                    ? 'This customer company also sells to another site. Its users can hold a role on each side.'
                    : 'Turn on when this customer company also sells to another site on the platform.'}
              </p>
            </div>
            <Switch
              checked={on || arming}
              disabled={loading || saving || Boolean(error)}
              aria-label="Also trades as supplier"
              onCheckedChange={(checked) => {
                if (checked) {
                  setArming(true);
                } else if (on) {
                  void apply({ is_also_supplier: false });
                } else {
                  setArming(false);
                }
              }}
            />
          </div>

          {error && (
            <p className="mt-2 flex items-center gap-1 text-sm text-destructive">
              <AlertCircle className="h-4 w-4" /> {error}
            </p>
          )}

          {/* A supplier-typed row with this name already exists: the duplicate a
              feed mints when the marker and code were missing. Shown, not
              merged — folding it is a separate repair. */}
          {state && state.name_twins.length > 0 && (
            <div
              className="mt-3 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm"
              data-testid="dual-identity-name-twins"
            >
              <p className="flex items-center gap-1 font-medium text-amber-800">
                <AlertTriangle className="h-4 w-4" />
                A supplier company with this name already exists
              </p>
              <ul className="mt-1 space-y-0.5 text-amber-800">
                {state.name_twins.map((twin) => (
                  <li key={twin.id} className="flex items-center justify-between gap-2">
                    <span className="truncate">{twin.name} <span className="text-xs">(id {twin.id})</span></span>
                    <span className="shrink-0 text-xs">
                      {twin.supplier_code ? <span className="font-mono">{twin.supplier_code}</span> : 'no code'}
                      {' · '}{twin.relationship_count} customer{twin.relationship_count === 1 ? '' : 's'}
                    </span>
                  </li>
                ))}
              </ul>
              <p className="mt-1 text-xs text-amber-800">
                Supplier feeds may already be landing orders on it. Marking this company does not
                merge the two; fold the duplicate as a separate step.
              </p>
            </div>
          )}

          {arming && !on && (
            <div className="mt-3 space-y-3 rounded-md border p-3" data-testid="dual-identity-form">
              <div>
                <p className="text-xs text-muted-foreground mb-1">Supplies (customer)</p>
                {customer ? (
                  <div className="flex items-center justify-between gap-2 text-sm">
                    <span className="font-medium truncate">{customer.name}</span>
                    <Button variant="ghost" size="sm" onClick={() => setCustomer(null)}>Change</Button>
                  </div>
                ) : (
                  <>
                    <Input
                      placeholder="Type to search customer companies…"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      autoFocus
                    />
                    <div className="mt-2 max-h-40 overflow-y-auto rounded-md border divide-y">
                      {searching && candidates.length === 0 ? (
                        <div className="p-2 text-sm text-muted-foreground">Searching…</div>
                      ) : candidates.length === 0 ? (
                        <div className="p-2 text-sm text-muted-foreground">No customer companies match &quot;{search}&quot;</div>
                      ) : (
                        candidates.map((c) => (
                          <button
                            key={c.id}
                            type="button"
                            onClick={() => setCustomer(c)}
                            className="w-full flex items-center justify-between gap-2 px-3 py-2 text-left text-sm hover:bg-muted"
                          >
                            <span className="truncate">{c.name}</span>
                            {c.group_name && (
                              <span className="text-xs text-muted-foreground shrink-0">{c.group_name}</span>
                            )}
                          </button>
                        ))
                      )}
                    </div>
                  </>
                )}
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-1">Supplier code</p>
                <Input
                  placeholder="The code the customer's ERP uses for this company"
                  value={supplierCode}
                  onChange={(e) => setSupplierCode(e.target.value)}
                  maxLength={50}
                />
                <p className="mt-1 text-xs text-muted-foreground">
                  Required. Without it the next supplier feed would create a duplicate company.
                </p>
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" size="sm" disabled={saving} onClick={() => setArming(false)}>
                  Cancel
                </Button>
                <Button
                  size="sm"
                  disabled={!canSubmit}
                  onClick={() => customer && apply({
                    is_also_supplier: true, customer_id: customer.id, supplier_code: supplierCode.trim(),
                  })}
                >
                  {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CheckCircle className="h-4 w-4 mr-2" />}
                  Mark as supplier
                </Button>
              </div>
            </div>
          )}

          {on && state && (
            <div className="mt-3">
              <p className="text-xs text-muted-foreground mb-1">Supplies</p>
              {state.supplier_relationships.length === 0 ? (
                <p className="text-sm text-muted-foreground">No supplier relationships recorded.</p>
              ) : (
                <ul className="divide-y rounded-md border">
                  {state.supplier_relationships.map((rel) => (
                    <li key={rel.id} className="flex items-center justify-between gap-2 px-3 py-2 text-sm">
                      <span className="truncate">{rel.customer_name}</span>
                      <span className="flex items-center gap-2 shrink-0">
                        <span className="font-mono text-xs">{rel.supplier_code || '—'}</span>
                        <Badge variant={rel.is_active ? 'default' : 'secondary'} className="text-xs">
                          {rel.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </span>
                    </li>
                  ))}
                </ul>
              )}
              <p className="mt-2 text-xs text-muted-foreground">
                Turning this off keeps the relationships and their orders; only the supplier side is removed.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
